<template>
  <div class="space-y-6">
    <div
      v-if="
        printBudgetSummary &&
        printBudgetSummary.totalBalance > 0 &&
        paymentState === 'form'
      "
      class="flex justify-start"
    >
      <button
        type="button"
        class="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
        @click="$emit('back')"
      >
        {{ t('print.back') }}
      </button>
    </div>
    <div
      v-if="
        printBudgetSummary &&
        printBudgetSummary.totalBalance > 0 &&
        canCoverWithBudget &&
        paymentState === 'form'
      "
      class="bg-[white] rounded-lg p-5 shadow-sm mt-4"
    >
      <div class="flex justify-between items-center mb-5">
        <h2 class="text-lg font-semibold text-gray-900">
          Today's Budget Spend
        </h2>
        <div class="text-xl font-bold text-blue-600">
          ${{ orderTotal.toFixed(2) }}
        </div>
      </div>

      <button
        type="button"
        class="px-5 py-2 bg-primary text-[white] rounded-full hover:bg-primary-dark transition-colors text-sm font-medium mb-3"
        :disabled="!isSubmitEnabled || isProcessing"
        @click="handlePrintBudgetPayment()"
      >
        <span v-if="isProcessing">Processing...</span>
        <span v-else>Confirm Order</span>
      </button>

      <div class="text-xs text-[gray]">
        By proceeding with your purchase, you acknowledge that this item is
        final sale and non-refundable.
      </div>
    </div>
    <div
      v-else-if="paymentState === 'form'"
      class="bg-white rounded-lg p-4 lg:p-6 shadow-sm"
    >
      <h2 class="text-xl lg:text-2xl font-medium mb-4 lg:mb-6">
        {{ t('print.payment-method') }}
      </h2>

      <div class="mb-6">
        <p
          v-if="paymentMethod === 'organization'"
          class="text-sm text-gray-500 mb-4"
        >
          <template v-if="isFullyFinancedPrint">
            Sponsored by {{ organizationName }}
          </template>
          <template v-else>
            {{ t('print.org-payment-method-description') }}
          </template>
        </p>
        <p
          v-else-if="paymentMethod === 'print-budget'"
          class="text-sm text-gray-500 mb-4"
        >
          Pay using your allocated print budget funds
        </p>

        <div class="space-y-4">
          <div
            v-if="showPrintBudgetOption"
            class="border rounded-lg p-4"
            :class="
              paymentMethod === 'print-budget'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300'
            "
          >
            <label class="flex items-center cursor-pointer">
              <input
                v-model="paymentMethod"
                type="radio"
                value="print-budget"
                class="mr-3"
              />
              <div class="flex-1">
                <div class="font-medium">Print Budget</div>
                <div class="text-sm text-gray-600">
                  Use your allocated print budget (${{
                    (printBudgetSummary.totalBalance / 100).toFixed(2)
                  }}
                  available)
                </div>
              </div>
            </label>
          </div>
          <div
            v-if="showOrganizationOption"
            class="border rounded-lg p-4"
            :class="
              paymentMethod === 'organization'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300'
            "
          >
            <label class="flex items-center cursor-pointer">
              <input
                v-model="paymentMethod"
                type="radio"
                value="organization"
                class="mr-3"
              />
              <div class="flex items-center">
                <Icon :icon="BuildingSvg" class="w-5 h-5 mr-2 text-gray-600" />
                <span class="font-medium"
                  >{{ t('print.sponsored-by-organization') }}
                </span>
              </div>
            </label>
          </div>

          <div
            v-if="showSelfPayOption"
            class="border rounded-lg p-4"
            :class="
              paymentMethod === 'personal'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300'
            "
          >
            <label class="flex items-center cursor-pointer">
              <input
                v-model="paymentMethod"
                type="radio"
                value="personal"
                class="mr-3"
              />
              <div class="flex items-center">
                <Icon :icon="CardSvg" class="w-5 h-5 mr-2 text-gray-600" />
                <span class="font-medium">{{ t('print.card') }}</span>
              </div>
            </label>
          </div>
        </div>
      </div>

      <div v-if="paymentMethod === 'personal'">
        <div class="mb-4 lg:mb-6">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="sameAsShipping"
              type="checkbox"
              class="w-4 h-4 lg:w-5 lg:h-5 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span class="text-sm lg:text-base">{{
              t('print.same-as-shipping')
            }}</span>
          </label>
        </div>

        <div
          v-if="!sameAsShipping"
          class="bg-[white] rounded-lg p-4 lg:p-6 shadow-sm space-y-4"
        >
          <h3 class="text-lg font-medium">{{ t('print.billing-address') }}</h3>

          <div class="grid gap-4">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray mb-1">
                  {{ t('print.first-name') }} <span class="text-red">*</span>
                </label>
                <input
                  v-model="billingAddress.firstName"
                  type="text"
                  class="w-full p-2 border rounded-lg"
                  :class="
                    !sameAsShipping && !billingAddress.firstName && error
                      ? 'border-red'
                      : 'border-gray'
                  "
                  required
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray mb-1">{{
                  t('print.last-name')
                }}</label>
                <input
                  v-model="billingAddress.lastName"
                  type="text"
                  class="w-full p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray mb-1">{{
                t('print.address-line-1')
              }}</label>
              <input
                v-model="billingAddress.line1"
                type="text"
                class="w-full p-2 border border-gray-300 rounded-lg"
                required
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray mb-1">{{
                t('print.address-line-2')
              }}</label>
              <input
                v-model="billingAddress.line2"
                type="text"
                class="w-full p-2 border border-gray-300 rounded-lg"
              />
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray mb-1">{{
                  t('print.city')
                }}</label>
                <input
                  v-model="billingAddress.city"
                  type="text"
                  class="w-full p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray mb-1">{{
                  t('print.state')
                }}</label>
                <input
                  v-model="billingAddress.state"
                  type="text"
                  class="w-full p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray mb-1">{{
                  t('print.postal-code')
                }}</label>
                <input
                  v-model="billingAddress.postal_code"
                  type="text"
                  class="w-full p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray mb-1">{{
                  t('print.country')
                }}</label>
                <input
                  v-model="billingAddress.country"
                  type="text"
                  class="w-full p-2 border border-gray-300 rounded-lg bg-gray-100"
                  disabled
                />
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray mb-1">{{
                t('print.phone')
              }}</label>
              <div class="flex gap-2">
                <select
                  v-model="billingAddress.phoneCountryCode"
                  class="w-20 p-2 border border-gray-300 rounded-lg"
                >
                  <option value="+1">+1</option>
                </select>
                <input
                  v-model="billingAddress.phone"
                  type="tel"
                  class="flex-1 p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="!isBillingAddressValid && !sameAsShipping && error"
          class="text-red text-sm mt-2"
        >
          {{ t('print.please-onboarding-complete-billing-address') }}
        </div>

        <div id="payment-element" class="mt-4"></div>

        <div v-if="!isFormValid && error" class="text-red text-sm mt-2">
          {{ t('print.please-onboarding-complete-payment-info') }}
        </div>
      </div>

      <div v-if="error" class="text-red text-sm mt-2">
        {{ error }}
      </div>
      <div class="flex justify-between pt-4 lg:pt-6">
        <button
          type="button"
          class="px-4 py-2 lg:px-6 text-gray hover:text-gray"
          @click="$emit('back')"
        >
          {{ t('print.back') }}
        </button>
        <button
          type="submit"
          class="px-4 py-2 lg:px-6 bg-primary text-[white] rounded-lg transition-colors"
          :class="
            isSubmitEnabled
              ? 'hover:bg-primary-dark'
              : 'opacity-50 cursor-not-allowed bg-gray'
          "
          :disabled="!isSubmitEnabled || isProcessing"
          @click="
            paymentMethod === 'organization'
              ? handleOrgPayment()
              : handleSubmit()
          "
        >
          <span v-if="isProcessing">{{ t('print.processing') }}...</span>
          <span v-else>{{ t('print.place-order') }}</span>
        </button>
      </div>
    </div>

    <div v-else-if="paymentState === 'processing'" class="text-center py-12">
      <div
        class="motion-safe:animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"
      ></div>
      <h3 class="text-xl font-medium mb-2">
        {{ t('print.processing-payment') }}
      </h3>
      <p class="text-gray">{{ t('print.please-dont-close') }}</p>
    </div>

    <div v-else-if="paymentState === 'success'" class="text-center py-12 px-8">
      <div
        class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
      >
        <Icon
          :icon="CheckCircleSvg"
          class="text-primary"
          :size="IconSize['3XL']"
        />
      </div>
      <h3 class="text-3xl font-medium mb-2">
        {{ t('print.order-successful') }}
      </h3>
      <p class="text-gray text-xl mb-8">
        {{ t('print.order-confirmation-email') }}
      </p>
      <button
        class="bg-primary text-[white] px-16 py-3 rounded-full hover:bg-[#3b68db] transition-colors"
        @click="goToConfirmation"
      >
        {{ t('print.store-home') }}
      </button>
    </div>

    <div v-else-if="paymentState === 'error'" class="text-center py-12">
      <div
        class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"
      >
        <Icon :icon="XMarkSvg" class="text-red" :size="IconSize.XL" />
      </div>
      <h3 class="text-xl font-medium mb-2 text-white">
        {{ t('print.payment-failed') }}
      </h3>
      <p class="text-red mb-6">{{ error }}</p>
      <button
        class="px-6 py-2 bg-primary text-[white] rounded-full hover:bg-primary-dark"
        @click="resetPaymentState"
      >
        {{ t('print.try-again') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRuntimeConfig } from '#imports';
import { loadStripe } from '@stripe/stripe-js';
import { usePrintStore } from '~/store/print';
import {
  apiCreatePrintCheckoutSession,
  apiGetPrintBudgetSummary,
  apiCreatePrintBudgetPayment,
  apiGetPrintConfig,
  type PrintBudgetSummary,
} from '~/api/print';
import XMarkSvg from '~/assets/icons/xmark.svg?component';
import BuildingSvg from '~/assets/icons/building.svg?component';
import CardSvg from '~/assets/icons/card.svg?component';
import CheckCircleSvg from '~/assets/icons/check-circle.svg?component';
import { useOrganizationStore } from '~/store/organization';
import { Icon, IconSize } from '~/components/icon';

const { t } = useI18n();
const config = useRuntimeConfig();
const printStore = usePrintStore();
const organizationStore = useOrganizationStore();

const props = defineProps<{
  printBudgetSummary?: PrintBudgetSummary | null;
  canCoverWithBudget?: boolean;
  isBudgetExceeded?: boolean;
  orderTotal?: number;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'complete'): void;
  (e: 'payment-success'): void;
}>();

const error = ref<string>('');
const isProcessing = ref(false);
const sameAsShipping = ref(true);

const printBudgetSummary = computed(() => {
  const summary = props.printBudgetSummary;
  return summary;
});
const canCoverWithBudget = computed(() => {
  const canCover = props.canCoverWithBudget || false;
  return canCover;
});
const isBudgetExceeded = computed(() => props.isBudgetExceeded || false);
const orderTotal = computed(() => props.orderTotal || 0);

const isOrgFinancingAvailable = computed(
  () => organizationStore.organization?.financingAvailable ?? false,
);

const isFullyFinancedPrint = computed(
  () => organizationStore.organization?.fullyFinancedPrint ?? false,
);

const hasPrintBudgetsEnabled = computed(() => {
  const enabled = organizationStore.organization?.hasPrintBudgets ?? false;
  return enabled;
});

const isZeroAmountOrder = computed(() => {
  const isZero = orderTotal.value <= 0;
  return isZero;
});

const cartHasItemsRequiringPayment = computed(() => {
  const items = Object.values(printStore.selectedItems);
  const hasRequiredPaymentItems = items.some((item) => {
    const requiresPayment = item.printProduct.requiresPayment === true;
    return requiresPayment;
  });

  return hasRequiredPaymentItems;
});

const requiresNoPayment = computed(() => {
  const isFullyFinanced = isFullyFinancedPrint.value;
  const isZeroAmount = isZeroAmountOrder.value;
  const hasRequiredPaymentItems = cartHasItemsRequiringPayment.value;
  const noPaymentNeeded =
    (isFullyFinanced && !hasRequiredPaymentItems) || isZeroAmount;
  return noPaymentNeeded;
});

const defaultPaymentMethod = computed(() => {
  if (
    hasPrintBudgetsEnabled.value &&
    printBudgetSummary.value &&
    printBudgetSummary.value.totalBalance > 0 &&
    canCoverWithBudget.value
  ) {
    return 'print-budget';
  }
  if (isOrgFinancingAvailable.value || requiresNoPayment.value) {
    return 'organization';
  }
  return 'personal';
});

const paymentMethod = ref(defaultPaymentMethod.value);

const organizationName = computed(
  () => organizationStore.organization?.name || '',
);

const showPrintBudgetOption = computed(() => {
  const shouldShow =
    hasPrintBudgetsEnabled.value &&
    printBudgetSummary.value &&
    printBudgetSummary.value.totalBalance > 0 &&
    canCoverWithBudget.value;
  return shouldShow;
});

const showOrganizationOption = computed(() => {
  const shouldShow =
    (isOrgFinancingAvailable.value || requiresNoPayment.value) &&
    (!printBudgetSummary.value || !canCoverWithBudget.value);
  return shouldShow;
});

const showSelfPayOption = computed(() => {
  const shouldShow =
    !requiresNoPayment.value &&
    (!hasPrintBudgetsEnabled.value ||
      !printBudgetSummary.value ||
      !canCoverWithBudget.value);
  return shouldShow;
});

const paymentMethods = computed(() => [
  { value: 'personal', label: t('print.pay-with-card') },
  { value: 'organization', label: t('print.pay-with-organization') },
]);

const billingAddress = ref({
  firstName: '',
  lastName: '',
  email: '',
  businessName: '',
  phoneCountryCode: '+1',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'US',
});

const finalBillingAddress = computed(() => {
  if (sameAsShipping.value) {
    const { shippingAddress } = printStore.checkout;
    const [firstName, ...lastNameParts] = shippingAddress.name.split(' ');
    return {
      firstName,
      lastName: lastNameParts.join(' '),
      email: shippingAddress.email,
      businessName: shippingAddress.businessName,
      phone: shippingAddress.phone,
      line1: shippingAddress.address.line1,
      line2: shippingAddress.address.line2 || '',
      city: shippingAddress.address.city,
      state: shippingAddress.address.state,
      postal_code: shippingAddress.address.postal_code,
      country: shippingAddress.address.country,
    };
  }
  return billingAddress.value;
});

let stripe: any = null;
let elements: any = null;
let paymentElement: any = null;
let clientSecret: string | null = null;

type PaymentState = 'form' | 'processing' | 'success' | 'error';
const paymentState = ref<PaymentState>('form');

const isFormValid = ref(false);

const isBillingAddressValid = computed(() => {
  if (sameAsShipping.value) return true;

  return (
    billingAddress.value.firstName.trim() !== '' &&
    billingAddress.value.lastName.trim() !== '' &&
    billingAddress.value.line1.trim() !== '' &&
    billingAddress.value.city.trim() !== '' &&
    billingAddress.value.state.trim() !== '' &&
    billingAddress.value.postal_code.trim() !== '' &&
    billingAddress.value.phone.trim() !== ''
  );
});

const buildRequestPayload = (includeOrgFinancing = false) => {
  printStore.syncCurrencyWithAddress();

  return {
    items: Object.entries(printStore.selectedItems).map(
      ([key, item]: [string, any]) => {
        const quantity = printStore.quantities[key] || 0;
        let price = item.printProduct.basePrice;

        if (
          item.printProduct.priceOverride !== null &&
          item.printProduct.priceOverride !== undefined
        ) {
          price = item.printProduct.priceOverride;
        } else if (item.printProduct.priceTiers?.length) {
          const applicableTier = [...item.printProduct.priceTiers]
            .sort((a, b) => b.quantity - a.quantity)
            .find((tier) => quantity >= tier.quantity);

          if (applicableTier) {
            price = applicableTier.pricePerUnit;
          }
        }

        return {
          contentFilePrintId: Number(item.printProduct.contentFilePrintId),
          quantity,
          price,
          previewUrl: item.previewUrl || item.imageUrl,
          fileUrl: item.fileUrl || item.previewUrl || item.imageUrl,
          customization: item.logoId ? { logoId: item.logoId } : undefined,
        };
      },
    ),

    shippingDetails: {
      fullName: printStore.checkout.shippingAddress.name,
      addressLine1: printStore.checkout.shippingAddress.address.line1,
      addressLine2: printStore.checkout.shippingAddress.address.line2 || '',
      city: printStore.checkout.shippingAddress.address.city,
      state: printStore.checkout.shippingAddress.address.state,
      postalCode: printStore.checkout.shippingAddress.address.postal_code,
      country: printStore.checkout.shippingAddress.address.country,
      phone: printStore.checkout.shippingAddress.phone,
      businessName: printStore.checkout.shippingAddress.businessName || '',
    },

    billingDetails: {
      fullName: sameAsShipping.value
        ? printStore.checkout.shippingAddress.name
        : `${billingAddress.value.firstName} ${billingAddress.value.lastName}`,
      addressLine1: finalBillingAddress.value.line1,
      addressLine2: finalBillingAddress.value.line2,
      city: finalBillingAddress.value.city,
      state: finalBillingAddress.value.state,
      postalCode: finalBillingAddress.value.postal_code,
      country: finalBillingAddress.value.country,
      phone: sameAsShipping.value
        ? printStore.checkout.shippingAddress.phone
        : `${billingAddress.value.phoneCountryCode}${billingAddress.value.phone}`,
    },

    shippingMethod: {
      id: printStore.checkout.shippingRate?.service || '',
      name: printStore.checkout.shippingRate?.service || '',
      price:
        printStore.checkout.shippingRate?.originalAmount ||
        printStore.checkout.shippingRate?.amount ||
        0,
      estimatedDays: printStore.checkout.shippingRate?.estimatedDays || '',
    },
    subtotalAmount: printStore.subtotal,
    taxAmount: printStore.checkout.taxAmount,
    currency: printStore.checkout.currency,
    displayShippingAmount: printStore.checkout.shippingRate?.amount || 0,
    useOrgFinancing: Boolean(includeOrgFinancing),
    email: printStore.checkout.shippingAddress.email,
  };
};

const initializePaymentForm = async () => {
  if (paymentElement) {
    paymentElement.unmount();
    paymentElement = null;
  }
  elements = null;

  if (!stripe) {
    stripe = await loadStripe(config.public.stripePublishableKey);
    if (!stripe) {
      throw new Error('Failed to load Stripe');
    }
  }

  const requestPayload = buildRequestPayload();

  const response = await apiCreatePrintCheckoutSession(requestPayload);
  ({ clientSecret } = response);
  if (
    response.processingFeeAmount !== undefined ||
    response.flatFeeAmount !== undefined
  ) {
    printStore.setFees({
      processingFeeAmount: response.processingFeeAmount,
      flatFeeAmount: response.flatFeeAmount,
      flatFeeBreakdown: response.flatFeeBreakdown,
    });
  }

  elements = stripe.elements({
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#4374F3',
        colorBackground: '#ffffff',
        colorText: '#30313d',
        colorDanger: '#df1b41',
        fontFamily: 'system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '4px',
      },
    },
  });

  paymentElement = elements.create('payment', {
    layout: 'tabs',
    paymentMethodOrder: ['card'],
    defaultValues: {
      billingDetails: {
        address: {
          country: 'US',
        },
      },
    },
    wallets: {
      applePay: 'never',
      googlePay: 'never',
    },
  });

  const mountElement = document.querySelector('#payment-element');
  if (!mountElement) {
    return false;
  }

  paymentElement.mount('#payment-element');

  paymentElement.on('change', (event: any) => {
    error.value = event.error ? event.error.message : '';
    isFormValid.value = event.complete || false;
  });

  return true;
};

onMounted(async () => {
  if (!printStore.checkout.currency) {
    printStore.initCurrency();
  }

  let processingFeeRate = 0;
  const config = await apiGetPrintConfig();
  processingFeeRate = config.processingFeeRate || 0;

  let totalFlatFee = 0;
  const breakdown: Array<{
    productId: number;
    productName: string;
    feeAmount: number;
    feeLabel: string;
  }> = [];

  const entries = Object.entries(printStore.selectedItems);

  for (const [key, item] of Object.entries(printStore.selectedItems)) {
    const quantity = printStore.quantities[key] || 0;

    if (quantity <= 0 || !(item as any).printProduct) {
      continue;
    }

    const product = (item as any).printProduct;
    if (!product.fees || !Array.isArray(product.fees)) {
      continue;
    }

    for (const fee of product.fees) {
      if (fee.amount && fee.amount > 0) {
        totalFlatFee += fee.amount;
        breakdown.push({
          productId: product.id,
          productName: product.description,
          feeAmount: fee.amount,
          feeLabel: fee.label,
        });
      }
    }
  }

  let processingFeeAmount = 0;
  if (processingFeeRate > 0) {
    const { subtotal } = printStore;
    const shipping = printStore.checkout.shippingRate?.amount || 0;
    const tax = printStore.checkout.taxAmount || 0;

    const orderTotalBeforeFee = subtotal + shipping + tax + totalFlatFee;

    const totalWithProcessingFee =
      orderTotalBeforeFee / (1 - processingFeeRate);
    processingFeeAmount =
      Math.round((totalWithProcessingFee - orderTotalBeforeFee) * 100) / 100;
  }

  if (totalFlatFee > 0 || breakdown.length > 0 || processingFeeAmount > 0) {
    printStore.setFees({
      flatFeeAmount: totalFlatFee,
      flatFeeBreakdown: breakdown,
      processingFeeAmount,
    });
  }

  if (paymentMethod.value === 'personal') {
    await nextTick();
    await initializePaymentForm();
  }
});

onUnmounted(() => {
  if (paymentElement) {
    paymentElement.unmount();
    paymentElement = null;
  }
  elements = null;
  clientSecret = null;
});

const isSubmitEnabled = computed(() => {
  if (
    paymentMethod.value === 'organization' ||
    paymentMethod.value === 'print-budget'
  ) {
    return !isProcessing.value;
  }
  return (
    isFormValid.value && isBillingAddressValid.value && !isProcessing.value
  );
});

const handleOrgPayment = async () => {
  isProcessing.value = true;
  error.value = '';
  paymentState.value = 'processing';

  try {
    const requestPayload = buildRequestPayload(true);
    const response = await apiCreatePrintCheckoutSession(requestPayload);

    // Update fees in store if returned
    if (
      response.processingFeeAmount !== undefined ||
      response.flatFeeAmount !== undefined
    ) {
      printStore.setFees({
        processingFeeAmount: response.processingFeeAmount,
        flatFeeAmount: response.flatFeeAmount,
        flatFeeBreakdown: response.flatFeeBreakdown,
      });
    }

    if (response.clientSecret === 'fully_financed_no_payment_needed') {
      paymentState.value = 'success';
      emit('payment-success');
      return;
    }

    paymentState.value = 'success';
    emit('payment-success');
  } catch {
    error.value = 'Failed to process organization payment. Please try again.';
    paymentState.value = 'error';
  } finally {
    isProcessing.value = false;
  }
};

const handlePrintBudgetPayment = async () => {
  isProcessing.value = true;
  error.value = '';
  paymentState.value = 'processing';

  try {
    if (!canCoverWithBudget.value) {
      throw new Error('Insufficient print budget balance');
    }

    const requestPayload = buildRequestPayload(false);
    const response = await apiCreatePrintBudgetPayment({
      orderData: requestPayload,
      usePrintBudget: true,
    });

    // Update fees in store if returned
    if (
      response.processingFeeAmount !== undefined ||
      response.flatFeeAmount !== undefined
    ) {
      printStore.setFees({
        processingFeeAmount: response.processingFeeAmount,
        flatFeeAmount: response.flatFeeAmount,
        flatFeeBreakdown: response.flatFeeBreakdown,
      });
    }

    paymentState.value = 'success';
    emit('payment-success');
  } catch (err: any) {
    error.value =
      err.message ||
      'Failed to process print budget payment. Please try again.';
    paymentState.value = 'error';
  } finally {
    isProcessing.value = false;
  }
};

const handleSubmit = async () => {
  if (paymentMethod.value === 'print-budget') {
    await handlePrintBudgetPayment();
    return;
  }

  if (paymentMethod.value === 'organization') {
    await handleOrgPayment();
    return;
  }

  if (!stripe || !elements || !paymentElement || !clientSecret) {
    error.value = t('print.payment-not-ready');
    return;
  }

  if (!isFormValid.value) {
    error.value = t('print.please-onboarding-complete-payment-info');
    return;
  }

  if (!isBillingAddressValid.value) {
    error.value = t('print.please-onboarding-complete-billing-address');
    return;
  }

  try {
    isProcessing.value = true;
    error.value = '';

    const { error: submitError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        payment_method_data: {
          billing_details: {
            address: {
              line1: finalBillingAddress.value.line1,
              line2: finalBillingAddress.value.line2,
              city: finalBillingAddress.value.city,
              state: finalBillingAddress.value.state,
              postal_code: finalBillingAddress.value.postal_code,
              country: finalBillingAddress.value.country,
            },
          },
        },
        return_url: undefined,
      },
      redirect: 'if_required',
    });

    if (submitError) {
      error.value = submitError.message || t('print.payment-failed');
      paymentState.value = 'error';
    } else {
      if (paymentElement) {
        paymentElement.unmount();
        paymentElement = null;
      }
      elements = null;

      paymentState.value = 'success';
    }
  } catch {
    paymentState.value = 'error';
  } finally {
    isProcessing.value = false;
  }
};

const resetPaymentState = async () => {
  paymentState.value = 'form';
  error.value = '';
  await initializePaymentForm();
};

const goToConfirmation = () => {
  const navigatePromise = navigateTo('/print?tab=history');

  if (
    navigatePromise &&
    typeof navigatePromise === 'object' &&
    'then' in navigatePromise
  ) {
    navigatePromise.then(() => {
      printStore.resetAll();
    });
  } else {
    setTimeout(() => {
      printStore.resetAll();
    }, 100);
  }
};

watch(sameAsShipping, (newValue) => {
  if (!newValue) {
    billingAddress.value = {
      firstName: '',
      lastName: '',
      businessName: '',
      phoneCountryCode: '+1',
      phone: '',
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
    };
  }
});

watch(paymentMethod, async (newValue) => {
  if (newValue === 'personal' && !isFullyFinancedPrint.value) {
    error.value = '';

    await nextTick();
    await initializePaymentForm();
  } else {
    if (paymentElement) {
      paymentElement.unmount();
      paymentElement = null;
    }
    elements = null;
    clientSecret = null;
  }
});

watch(canCoverWithBudget, (newValue) => {
  if (!newValue && paymentMethod.value === 'print-budget') {
    const newPaymentMethod =
      isOrgFinancingAvailable.value || isFullyFinancedPrint.value
        ? 'organization'
        : 'personal';
    paymentMethod.value = newPaymentMethod;
  }
});

watch(paymentMethod, async (newMethod, oldMethod) => {
  if (newMethod === 'personal' && oldMethod !== 'personal') {
    await nextTick();
    await initializePaymentForm();
  }

  if (oldMethod === 'personal' && newMethod !== 'personal') {
    if (paymentElement) {
      paymentElement.unmount();
      paymentElement = null;
    }
    if (elements) {
      elements = null;
    }
    clientSecret = null;
    isFormValid.value = false;
    error.value = '';
  }
});

watch(defaultPaymentMethod, (newValue) => {
  paymentMethod.value = newValue;
});
</script>

<style scoped>
:deep(#payment-element) {
  margin-top: 1rem;
}

:deep(.StripeElement) {
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background-color: white;
}
</style>
